import React, { useState, useEffect, useContext } from 'react';
import { useNavigate } from 'react-router-dom';
import '../assets/css/DashboardHome.css';
import AuthContext from '../context/AuthContext';
import { validateAdmin } from '../api/auth';
import { getDashboardStats } from '../api/dashboard';
import { fetchUsers } from '../api/users';
import { fetchPosts } from '../api/posts';
import { deleteUser } from '../api/deleteUser';

import { FontAwesomeIcon } from '@fortawesome/react-fontawesome';
import {
  faUsers,
  faFileAlt,
  faGift,
  faFlag,
  faChevronLeft,
  faChevronRight
} from '@fortawesome/free-solid-svg-icons';


const DashboardHome = () => {
  const navigate = useNavigate();
  const { logout } = useContext(AuthContext);

  const [activeWidget, setActiveWidget] = useState('totalUsers');
  const [user, setUser] = useState(null);
  const [stats, setStats] = useState({
    total_users: 0,
    total_posts: 0,
    total_sponsored_posts: 0,
    total_reports: 0
  });
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState('');


  const [previewImage, setPreviewImage] = useState(null);

  const openPreview = (imageUrl) => {
    setPreviewImage(imageUrl);
  };

  const closePreview = () => {
    setPreviewImage(null);
  };
  // Expanded state
  const [expandedData, setExpandedData] = useState([]);
  const [expLoading, setExpLoading] = useState(false);
  const [expError, setExpError] = useState('');
  const [expPage, setExpPage] = useState(1);
  const [expPagination, setExpPagination] = useState({
    current_page: 1,
    total_pages: 1
  });
  const [mediaIndex, setMediaIndex] = useState({});

  // 1) Validate + stats on mount
  useEffect(() => {
    (async () => {
      try {
        const u = await validateAdmin();
        setUser(u);
        const s = await getDashboardStats();
        setStats(s);
      } catch (err) {
        setError(err.message);
        await logout();
        navigate('/login');
      } finally {
        setLoading(false);
      }
    })();
  }, [logout, navigate]);

  // 2) Fetch details when widget or page changes
  useEffect(() => {
    setExpandedData([]);
    setExpError('');
    setExpLoading(true);

    const perPage = 8; // two rows of 4 cards each

    if (activeWidget === 'totalUsers') {
      fetchUsers(expPage, perPage)
        .then(res => {
          setExpandedData(res.data);
          setExpPagination(res.pagination);
        })
        .catch(err => setExpError(err.message))
        .finally(() => setExpLoading(false));
    } else if (activeWidget === 'totalPosts') {
      fetchPosts(expPage, perPage)
        .then(res => {
          setExpandedData(res.data);
          setExpPagination(res.pagination);
        })
        .catch(err => setExpError(err.message))
        .finally(() => setExpLoading(false));
    } else {
      setExpLoading(false);
    }
  }, [activeWidget, expPage]);

  if (loading) return <div className="dashboard-home">Loading…</div>;
  if (error) return <div className="dashboard-home">Error: {error}</div>;

  const handleDeleteUser = async (u) => {
    const ok = window.confirm(
      `Delete @${u.username} (${u.full_name})?\n\nThis cannot be undone.`
    );
    if (!ok) return;

    try {
      await deleteUser({ target_user_id: u.user_id });

      // remove from UI instantly
      setExpandedData(prev => prev.filter(x => x.user_id !== u.user_id));

      // optional: update stats
      setStats(s => ({
        ...s,
        total_users: Math.max(0, s.total_users - 1)
      }));
    } catch (err) {
      alert(err.message || 'Failed to delete user');
    }
  };

  const renderExpanded = () => {
    if (expLoading) return <p className="expanded-loading">Loading…</p>;
    if (expError) return <p className="expanded-error">Error: {expError}</p>;
    if (!expandedData.length) return null;

    // === Users grid ===
    if (activeWidget === 'totalUsers') {
      return (
        <>
          <div className="expanded-users-grid">
            {expandedData.map(u => (
              <div className="user-card" key={u.user_id}>
                <img
                  src={u.profile_picture_url}
                  alt={u.username}
                  className="user-card-avatar"
                  onClick={() => openPreview(u.profile_picture_url)}
                />

                <div className="user-card-info">
                  <strong className="user-card-name">{u.full_name}</strong>
                  <span className="user-card-username">@{u.username}</span>
                  <span className="user-card-country">{u.country}</span>
                </div>

                <button
                  className="user-delete-btn"
                  onClick={(e) => {
                    e.stopPropagation();
                    handleDeleteUser(u);
                  }}
                >
                  Delete
                </button>
              </div>
            ))}
          </div>

          <div className="expanded-pagination">
            <button
              disabled={expPagination.current_page === 1}
              onClick={() => setExpPage(expPagination.current_page - 1)}
            >
              <FontAwesomeIcon icon={faChevronLeft} /> Prev
            </button>
            <span>
              {expPagination.current_page} / {expPagination.total_pages}
            </span>
            <button
              disabled={expPagination.current_page === expPagination.total_pages}
              onClick={() => setExpPage(expPagination.current_page + 1)}
            >
              Next <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </>
      );
    }

    // === Posts grid ===
    if (activeWidget === 'totalPosts') {
      return (
        <>
          <div className="expanded-posts-list">
            {expandedData.map(p => {
              const medias = p.media || [];
              const idx = mediaIndex[p.post_id] || 0;
              const m = medias[idx];

              return (
                <div className="post-item" key={p.post_id}>
                  <div className="post-media">
                    {m && m.media_type === 'video' ? (
                      <video
                        src={m.image_url}
                        autoPlay
                        muted
                        loop
                        className="post-video"
                      />
                    ) : (
                      m && <img src={m.image_url} alt="" className="post-image" />
                    )}

                    {medias.length > 1 && (
                      <div className="post-carousel-controls">
                        <button
                          onClick={() =>
                            setMediaIndex(mi => ({
                              ...mi,
                              [p.post_id]: (idx - 1 + medias.length) % medias.length
                            }))
                          }
                        >
                          ‹
                        </button>
                        <button
                          onClick={() =>
                            setMediaIndex(mi => ({
                              ...mi,
                              [p.post_id]: (idx + 1) % medias.length
                            }))
                          }
                        >
                          ›
                        </button>
                      </div>
                    )}
                  </div>

                  <div className="post-info">
                    <strong>Post #{p.post_id}</strong>
                    <span>by {p.user?.full_name || 'Unknown'}</span>
                  </div>
                </div>
              );
            })}
          </div>

          <div className="expanded-pagination">
            <button
              disabled={expPagination.current_page === 1}
              onClick={() => setExpPage(expPagination.current_page - 1)}
            >
              <FontAwesomeIcon icon={faChevronLeft} /> Prev
            </button>
            <span>
              {expPagination.current_page} / {expPagination.total_pages}
            </span>
            <button
              disabled={expPagination.current_page === expPagination.total_pages}
              onClick={() => setExpPage(expPagination.current_page + 1)}
            >
              Next <FontAwesomeIcon icon={faChevronRight} />
            </button>
          </div>
        </>
      );
    }

    return null;
  };

  return (
    <div className="dashboard-home">
      <div className="dashboard-home-header">
        <h1>Welcome, {user.full_name}</h1>
        <p>
          <strong>@{user.username}</strong> ({user.roles.join(', ')}) — manage
          bio app with integrity.
        </p>
      </div>

      <div className="widget-section">
        {[
          {
            key: 'totalUsers',
            icon: faUsers,
            label: 'Total Users',
            value: stats.total_users,
            cls: 'users'
          },
          {
            key: 'totalPosts',
            icon: faFileAlt,
            label: 'Total Posts',
            value: stats.total_posts,
            cls: 'posts'
          },
          {
            key: 'activeSessions',
            icon: faGift,
            label: 'Total Sponsored',
            value: stats.total_sponsored_posts,
            cls: 'sponsored'
          },
          {
            key: 'newReports',
            icon: faFlag,
            label: 'Total Reports',
            value: stats.total_reports,
            cls: 'reports'
          }
        ].map(w => (
          <div
            key={w.key}
            className={`widget-item ${w.cls} ${
              activeWidget === w.key ? 'active' : ''
            }`}
            onClick={() => {
              setActiveWidget(w.key);
              setExpPage(1);
            }}
          >
            <FontAwesomeIcon icon={w.icon} className="widget-icon" />
            <div className="widget-text">
              <h2>{w.label}</h2>
              <p>{w.value}</p>
            </div>
          </div>
        ))}
      </div>


   
   
      {renderExpanded()}

      
      {previewImage && (
        <div className="image-preview-overlay" onClick={closePreview}>
          <div
            className="image-preview-container"
            onClick={(e) => e.stopPropagation()}
          >
            <button
              className="image-preview-close"
              onClick={closePreview}
            >
              ×
            </button>

            <img
              src={previewImage}
              alt="Preview"
              className="image-preview-large"
            />
          </div>
        </div>
      )}
      
    </div>
  );
};

export default DashboardHome;
